import React from 'react';
import { Copy, Link, Download, Check, XCircle } from 'lucide-react';
import type { SupportedLanguage, SnippetTheme, SnippetState } from './snippet.types';
import { LANGUAGE_OPTIONS, THEME_OPTIONS } from './snippet.utils';
import '../../styles/features/snippet/SnippetToolbar.css';

interface Props {
  title: string;
  language: SupportedLanguage;
  theme: SnippetTheme;
  copyCodeStatus: SnippetState['copyCodeStatus'];
  copyLinkStatus: SnippetState['copyLinkStatus'];
  downloadStatus: SnippetState['downloadStatus'];
  onTitleChange: (v: string) => void;
  onLanguageChange: (v: SupportedLanguage) => void;
  onThemeChange: (v: SnippetTheme) => void;
  onCopyCode: () => void;
  onCopyLink: () => void;
  onDownload: () => void;
}

const SnippetToolbar: React.FC<Props> = ({
  title,
  language,
  theme,
  copyCodeStatus,
  copyLinkStatus,
  downloadStatus,
  onTitleChange,
  onLanguageChange,
  onThemeChange,
  onCopyCode,
  onCopyLink,
  onDownload,
}) => {
  const getCopyCodeBtnClass = () => {
    if (copyCodeStatus === 'copied') return 'snippet-toolbar__btn snippet-toolbar__btn--success';
    if (copyCodeStatus === 'error') return 'snippet-toolbar__btn snippet-toolbar__btn--error';
    return 'snippet-toolbar__btn';
  };

  const getCopyLinkBtnClass = () => {
    if (copyLinkStatus === 'copied') return 'snippet-toolbar__btn snippet-toolbar__btn--success snippet-toolbar__btn--primary';
    if (copyLinkStatus === 'error') return 'snippet-toolbar__btn snippet-toolbar__btn--error';
    return 'snippet-toolbar__btn snippet-toolbar__btn--primary';
  };

  const getDownloadBtnClass = () => {
    if (downloadStatus === 'done') return 'snippet-toolbar__btn snippet-toolbar__btn--success';
    if (downloadStatus === 'error') return 'snippet-toolbar__btn snippet-toolbar__btn--error';
    return 'snippet-toolbar__btn';
  };

  const getCopyCodeIcon = () => {
    if (copyCodeStatus === 'copied') return <Check size={15} />;
    if (copyCodeStatus === 'error') return <XCircle size={15} />;
    return <Copy size={15} />;
  };

  const getCopyLinkIcon = () => {
    if (copyLinkStatus === 'copied') return <Check size={15} />;
    if (copyLinkStatus === 'error') return <XCircle size={15} />;
    return <Link size={15} />;
  };

  const getDownloadIcon = () => {
    if (downloadStatus === 'done') return <Check size={15} />;
    if (downloadStatus === 'error') return <XCircle size={15} />;
    return <Download size={15} />;
  };

  const getCopyCodeLabel = () => {
    if (copyCodeStatus === 'copied') return 'Copied!';
    if (copyCodeStatus === 'error') return 'Failed';
    return 'Copy Code';
  };

  const getCopyLinkLabel = () => {
    if (copyLinkStatus === 'copied') return 'Link Copied!';
    if (copyLinkStatus === 'error') return 'Failed';
    return 'Share Link';
  };

  const getDownloadLabel = () => {
    if (downloadStatus === 'loading') return 'Saving...';
    if (downloadStatus === 'done') return 'Saved!';
    if (downloadStatus === 'error') return 'Failed';
    return 'Download PNG';
  };

  return (
    <div className="snippet-toolbar" role="toolbar" aria-label="Snippet actions">
      {/* Title input */}
      <input
        type="text"
        className="snippet-toolbar__title-input"
        value={title}
        onChange={e => onTitleChange(e.target.value)}
        placeholder="Snippet title..."
        aria-label="Snippet title"
        maxLength={80}
      />

      {/* Language selector */}
      <select
        className="snippet-toolbar__select"
        value={language}
        onChange={e => onLanguageChange(e.target.value as SupportedLanguage)}
        aria-label="Select language"
      >
        {LANGUAGE_OPTIONS.map(opt => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>

      {/* Theme selector */}
      <select
        className="snippet-toolbar__select"
        value={theme}
        onChange={e => onThemeChange(e.target.value as SnippetTheme)}
        aria-label="Select theme"
      >
        {THEME_OPTIONS.map(opt => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>

      {/* Action buttons */}
      <div className="snippet-toolbar__actions">
        <button
          type="button"
          className={getCopyCodeBtnClass()}
          onClick={onCopyCode}
          aria-label="Copy code to clipboard"
          title="Copy Code"
        >
          {getCopyCodeIcon()}
          <span>{getCopyCodeLabel()}</span>
        </button>

        <button
          type="button"
          className={getCopyLinkBtnClass()}
          onClick={onCopyLink}
          aria-label="Save snippet and copy share link"
          title="Copy Share Link"
        >
          {getCopyLinkIcon()}
          <span>{getCopyLinkLabel()}</span>
        </button>

        <button
          type="button"
          className={getDownloadBtnClass()}
          onClick={onDownload}
          disabled={downloadStatus === 'loading'}
          aria-label="Download snippet as PNG image"
          title="Download PNG"
        >
          {getDownloadIcon()}
          <span>{getDownloadLabel()}</span>
        </button>
      </div>
    </div>
  );
};

export default SnippetToolbar;
